import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { pessoaService } from "@/services/api/PessoaService";
import { CreateAusenciaDTO } from "@/types/pessoa";

/**
 * Ausência/férias de uma pessoa sob a ótica da GESTÃO (admin marcando terceiros).
 *
 * - `useAusenciaPessoa`: query da ausência ativa (para pré-preencher o substituto).
 * - `useGestaoAusencia`: mutations de marcar/remover, que invalidam o cache
 *   relevante no sucesso (a query da própria ausência e o detalhe da pessoa).
 */

export const ausenciaPessoaKey = (pessoaId?: number) => ["pessoa-ausencia", pessoaId] as const;

export function useAusenciaPessoa(pessoaId?: number, enabled = true) {
  return useQuery({
    queryKey: ausenciaPessoaKey(pessoaId),
    queryFn: () => pessoaService.getPessoaAusencia(pessoaId!),
    enabled: !!pessoaId && enabled,
    staleTime: 1000 * 60,
  });
}

export function useGestaoAusencia(pessoaId?: number) {
  const queryClient = useQueryClient();

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ausenciaPessoaKey(pessoaId) });
    queryClient.invalidateQueries({ queryKey: ["pessoa-detail", pessoaId] });
  };

  const marcar = useMutation({
    mutationFn: (dto: CreateAusenciaDTO) => pessoaService.adicionarAusenciaPara(pessoaId!, dto),
    onSuccess: invalidar,
  });

  const remover = useMutation({
    mutationFn: () => pessoaService.removerAusenciaDe(pessoaId!),
    onSuccess: invalidar,
  });

  return { marcar, remover };
}
